import { PayloadAction } from "@reduxjs/toolkit";
import LocalStorageSurvey from "modules/survey/infrastructure/store/localStorage/survey.localStorage";
import { all, put, select, takeEvery } from "redux-saga/effects";

import { applicationStateSelectors } from "store/slices/applicationState/selectors";
import { EnvironmentState } from "store/slices/environment/slice";
import { environmentSelectors } from "store/slices/environment/selector";
import { RoomSelectorConfiguration } from "types/roomSelector";

import {
  Survey,
  SurveyQuestionResponse,
  SurveysAnswer,
} from "../../../domain/survey/survey.types";
import { surveysActions } from "./survey.actions";
import { getSurveyById, getSurveysByProject } from "./survey.api";
import {
  setEndSurveyLoading,
  setSurveyLoading,
  setSurveysLoading,
  setEndSurveysLoading,
  setAnswersFromLocalStorage,
  setSurveyIsFinish,
  setSurveys,
  setSurveyPagination,
  setSurveyQuestion,
} from "../slice/survey.slice";
import { surveySelectors } from "../slice/survey.selectors";

function* getSurveysByProjectSaga() {
  yield put(surveysActions.getSurveysByProjectInProgress());
  const projectId: string = yield select(applicationStateSelectors.projectID);
  const environment: EnvironmentState = yield select(
    environmentSelectors.environment,
  );
  const surveys: Array<Survey> = yield getSurveysByProject(
    projectId,
    environment.REACT_APP_API_REST_BASE_URI,
  );
  yield put(setSurveys(surveys));
  yield put(surveysActions.getSurveysByProjectDone());
  yield put(
    surveysActions.getSurveyByIdRequested({
      surveyID: surveys[0].id,
      order: 1,
    }),
  );
}

function* setLoadingSurveysByProjectSaga() {
  yield put(setSurveysLoading());
}

function* setEndLoadingSurveysByProjectSaga() {
  const surveysAnswersFromLocalStorage = LocalStorageSurvey.getAnswers();
  if (surveysAnswersFromLocalStorage) {
    const surveysAnswers: SurveysAnswer = JSON.parse(
      surveysAnswersFromLocalStorage,
    );
    if (Object.keys(surveysAnswers).length) {
      yield put(surveysActions.loadSurveysAnswersRequested(surveysAnswers));
    } else {
      yield put(setEndSurveysLoading());
    }
  } else {
    yield put(setEndSurveysLoading());
  }
}

function* loadSurveysAnswersSaga(action: PayloadAction<SurveysAnswer>) {
  const currentSurveyID = Object.keys(action.payload)[
    Object.keys(action.payload).length - 1
  ];
  const currentQuestionID = Object.keys(action.payload[currentSurveyID])[
    Object.keys(action.payload[currentSurveyID]).length - 1
  ];

  yield put(setAnswersFromLocalStorage(action.payload));
  yield put(
    surveysActions.resumeSurveyFromStateRequested({
      surveyID: currentSurveyID,
      questionID: currentQuestionID,
    }),
  );
}

function* loadSurveysAnswersEndSaga() {
  yield put(setEndSurveysLoading());
}

function* resumeSurveyFromStateSaga(
  action: PayloadAction<{ surveyID: string; questionID: string }>,
) {
  const environment: EnvironmentState = yield select(
    environmentSelectors.environment,
  );
  const survey: SurveyQuestionResponse = yield getSurveyById({
    survey_id: action.payload.surveyID,
    order: 1,
    baseUri: environment.REACT_APP_API_REST_BASE_URI,
  });
  const { data, pagination } = survey;
  const currentQuestionIndex = data.answers.findIndex(
    (question) => question.question_id === action.payload.questionID,
  );
  const currentQuestion = data.answers[currentQuestionIndex + 1];
  if (currentQuestion) {
    yield all([
      put(setSurveyQuestion(data)),
      put(setSurveyPagination(pagination)),
    ]);
    yield put(surveysActions.resumeSurveyFromStateDone());
  } else {
    const allSurveys: { id: string; questionsIDs: string[] }[] = yield select(
      surveySelectors.surveys,
    );
    const currentSurveyIndex = allSurveys.findIndex(
      (surveyItem) => surveyItem.id === action.payload.surveyID,
    );
    const currentSurvey = allSurveys[currentSurveyIndex + 1];
    if (currentSurvey) {
      yield put(surveysActions.resumeSurveyFromStateDone());
    } else {
      yield put(setSurveyIsFinish());
    }
  }
}

function* resumeSurveyFromStateDoneSaga() {
  yield put(setEndSurveysLoading());
}

function* getSurveyQuestionByIdRequestedSaga(
  action: PayloadAction<{ surveyID: string; order: number }>,
) {
  const environment: EnvironmentState = yield select(
    environmentSelectors.environment,
  );
  const roomSelectorConfiguration: RoomSelectorConfiguration = yield select(
    applicationStateSelectors.roomSelectorConfiguration,
  );
  if (!roomSelectorConfiguration?.featureFlags.surveyModule) return;
  try {
    yield put(surveysActions.getSurveyByIdInProgress());
    const survey: SurveyQuestionResponse = yield getSurveyById({
      survey_id: action.payload.surveyID,
      order: action.payload.order ?? 1,
      baseUri: environment.REACT_APP_API_REST_BASE_URI,
    });
    const { data, pagination } = survey;

    yield all([
      put(setSurveyPagination(pagination)),
      put(setSurveyQuestion(data)),
    ]);
    yield put(surveysActions.getSurveyByIdDone());
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("[survey.saga] Error in getSurveyByIdRequestedSaga:", error);
  }
}

function* setLoadingSurveyByIdSaga() {
  yield put(setSurveyLoading());
}
function* setEndLoadingSurveyByIdSaga() {
  yield put(setEndSurveyLoading());
}

export function* surveySaga() {
  yield takeEvery(
    surveysActions.getSurveysByProjectRequested,
    getSurveysByProjectSaga,
  );
  yield takeEvery(
    surveysActions.getSurveysByProjectInProgress,
    setLoadingSurveysByProjectSaga,
  );
  yield takeEvery(
    surveysActions.getSurveysByProjectDone,
    setEndLoadingSurveysByProjectSaga,
  );

  yield takeEvery(
    surveysActions.loadSurveysAnswersRequested,
    loadSurveysAnswersSaga,
  );
  yield takeEvery(
    surveysActions.loadSurveysAnswersEnd,
    loadSurveysAnswersEndSaga,
  );

  yield takeEvery(
    surveysActions.resumeSurveyFromStateRequested,
    resumeSurveyFromStateSaga,
  );
  yield takeEvery(
    surveysActions.resumeSurveyFromStateDone,
    resumeSurveyFromStateDoneSaga,
  );
  yield takeEvery(
    surveysActions.getSurveyByIdRequested,
    getSurveyQuestionByIdRequestedSaga,
  );
  yield takeEvery(
    surveysActions.getSurveyByIdInProgress,
    setLoadingSurveyByIdSaga,
  );
  yield takeEvery(
    surveysActions.getSurveyByIdDone,
    setEndLoadingSurveyByIdSaga,
  );
}
